import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callsAPI, ordersAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft, Phone, Clock, TrendingUp, User, ShoppingBag, Loader2, CheckCircle2, MessageSquare, Sparkles, Brain, Play, Volume2
} from "lucide-react";

const OUTCOME_STYLES = {
  order_placed: { label: "Order Placed", className: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
  transferred: { label: "Transferred", className: "bg-amber-50 text-amber-700 border border-amber-200" },
  abandoned: { label: "Abandoned", className: "bg-red-50 text-red-600 border border-red-200" },
};

function formatDuration(s) { return `${Math.floor(s/60)}m ${s%60}s`; }

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [recordings, setRecordings] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const callRes = await callsAPI.detail(id);
        setCall(callRes.data);
        if (callRes.data.ai_summary) setAiSummary(callRes.data.ai_summary);
        if (callRes.data.order_id) {
          const orderRes = await ordersAPI.detail(callRes.data.order_id);
          setOrder(orderRes.data);
        }
        // Fetch recordings
        try {
          const recRes = await callsAPI.recordings(id);
          setRecordings(recRes.data || []);
        } catch { /* no recordings */ }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleGenerateSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await callsAPI.aiSummary(id);
      setAiSummary(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSummary(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!call) return <div className="text-center py-12 text-slate-400">Call not found</div>;

  const outcomeStyle = OUTCOME_STYLES[call.outcome] || { label: call.outcome, className: "" };
  const conf = (call.confidence * 100).toFixed(0);

  return (
    <div className="space-y-5 fade-in" data-testid="call-detail-page">
      <Button variant="ghost" size="sm" className="text-slate-500 -ml-2 h-8" onClick={() => navigate("/calls")} data-testid="back-to-calls-button">
        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Calls
      </Button>

      {/* Call Metadata Strip */}
      <Card className="border-slate-100" data-testid="call-metadata-card">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center">
                <Phone className="w-5 h-5 text-primary" strokeWidth={2} />
              </div>
              <div>
                <p className="text-base font-bold font-mono text-slate-800 tracking-tight" data-testid="call-caller-number">{call.caller_number}</p>
                <p className="text-xs text-slate-400">{call.caller_name || "Unknown caller"}</p>
              </div>
            </div>
            <div className="hidden md:block w-px h-8 bg-slate-100" />
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Time</p>
              <p className="text-[13px] text-slate-700 font-medium">{new Date(call.start_time).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Duration</p>
              <p className="text-[13px] text-slate-700 font-medium flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" /> {formatDuration(call.duration_seconds)}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Outcome</p>
              <Badge variant="secondary" className={outcomeStyle.className}>{outcomeStyle.label}</Badge>
            </div>
            <div>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-0.5">Confidence</p>
              <div className="flex items-center gap-2">
                <div className="confidence-bar w-14">
                  <div className="confidence-bar-fill" style={{ width: `${conf}%`, background: conf >= 80 ? '#22c55e' : conf >= 60 ? '#f59e0b' : '#ef4444' }} />
                </div>
                <span className="text-[13px] text-slate-700 font-medium tabular-nums">{conf}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Transcript */}
        <Card className="lg:col-span-3 border-slate-100" data-testid="call-transcript-card">
          <CardHeader className="pb-3 px-5 pt-5">
            <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MessageSquare className="w-4 h-4 text-slate-400" /> Transcript
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <ScrollArea className="h-[420px] pr-2">
              <div className="space-y-3">
                {call.transcript?.map((msg, i) => (
                  <div key={i} className={`flex ${msg.speaker === "ai" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[85%] px-4 py-2.5 text-[13px] leading-relaxed ${
                      msg.speaker === "ai"
                        ? "bg-slate-50 text-slate-700 rounded-2xl rounded-bl-md border border-slate-100"
                        : "bg-primary text-white rounded-2xl rounded-br-md shadow-sm shadow-primary/20"
                    }`} data-testid={`transcript-msg-${i}`}>
                      <p className={`text-[9px] uppercase tracking-widest mb-1 font-semibold ${
                        msg.speaker === "ai" ? "text-slate-400" : "text-white/60"
                      }`}>
                        {msg.speaker === "ai" ? "AI Agent" : "Customer"}
                      </p>
                      {msg.text}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Order Summary */}
        <div className="lg:col-span-2 space-y-4">
          {order ? (
            <Card className="border-slate-100" data-testid="order-summary-card">
              <CardHeader className="pb-3 px-5 pt-5">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <ShoppingBag className="w-4 h-4 text-slate-400" /> Order
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Customer</p>
                  <p className="text-[13px] font-medium text-slate-800 flex items-center gap-1.5">
                    <User className="w-3 h-3 text-slate-400" /> {order.customer_name}
                  </p>
                  <p className="text-[11px] text-slate-400 font-mono">{order.customer_phone}</p>
                </div>
                <Separator className="bg-slate-100" />
                <div className="space-y-2">
                  <p className="text-[10px] text-slate-400 uppercase tracking-widest">Items</p>
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-[13px]">
                      <div className="text-slate-700">
                        <span>{item.quantity}x {item.name}</span>
                        {item.modifiers?.length > 0 && (
                          <span className="text-[11px] text-slate-400 ml-1">({item.modifiers.join(", ")})</span>
                        )}
                      </div>
                      <span className="font-medium text-slate-800 tabular-nums">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <Separator className="bg-slate-100" />
                <div className="space-y-1 text-[13px]">
                  <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="text-slate-600 tabular-nums">${order.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Tax</span><span className="text-slate-600 tabular-nums">${order.tax?.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-base pt-1">
                    <span className="text-slate-800">Total</span>
                    <span className="text-slate-900">${order.total?.toFixed(2)}</span>
                  </div>
                </div>
                <Separator className="bg-slate-100" />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest">POS Status</p>
                    <p className="text-[11px] font-mono text-slate-500 mt-0.5">{order.pos_order_id}</p>
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 badge-pill" data-testid="order-pos-status">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> {order.status}
                  </Badge>
                </div>
                {order.sms_sent && (
                  <Badge className="bg-blue-50 text-blue-600 border border-blue-200 badge-pill">SMS: {order.sms_status || "sent"}</Badge>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-100" data-testid="no-order-card">
              <CardContent className="p-8 text-center">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-2">
                  <ShoppingBag className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-[13px] text-slate-500">No order associated</p>
              </CardContent>
            </Card>
          )}

          {/* AI Call Summary */}
          <Card className="border-slate-100" data-testid="ai-summary-card">
            <CardHeader className="pb-3 px-5 pt-5 flex flex-row items-center justify-between">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Brain className="w-4 h-4 text-primary" /> AI Summary
              </CardTitle>
              {!aiSummary && (
                <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={handleGenerateSummary} disabled={loadingSummary} data-testid="generate-ai-summary">
                  {loadingSummary ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1 text-amber-500" />}
                  Generate
                </Button>
              )}
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {loadingSummary ? (
                <div className="flex items-center gap-2 py-4 justify-center text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Analyzing transcript...
                </div>
              ) : aiSummary ? (
                <div className="space-y-3">
                  <p className="text-[13px] text-slate-700 leading-relaxed">{aiSummary.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    {aiSummary.sentiment && (
                      <Badge variant="outline" className={`badge-pill text-[10px] ${
                        aiSummary.sentiment === "positive" ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                        : aiSummary.sentiment === "negative" ? "bg-red-50 text-red-600 border-red-200"
                        : "bg-slate-50 text-slate-500 border-slate-200"
                      }`}>
                        {aiSummary.sentiment}
                      </Badge>
                    )}
                    {aiSummary.customer_intent && (
                      <Badge variant="outline" className="badge-pill text-[10px] bg-blue-50 text-blue-600 border-blue-200">
                        {aiSummary.customer_intent?.replace(/_/g, " ")}
                      </Badge>
                    )}
                  </div>
                  {aiSummary.key_items_discussed?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">Key items</p>
                      <div className="flex flex-wrap gap-1">
                        {aiSummary.key_items_discussed.map((item, i) => (
                          <Badge key={i} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600">{item}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[12px] text-slate-400 text-center py-3">Click Generate to create an AI summary of this call</p>
              )}
            </CardContent>
          </Card>

          {/* Call Recording */}
          <Card className="border-slate-100" data-testid="call-recording-card">
            <CardHeader className="pb-3 px-5 pt-5">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Volume2 className="w-4 h-4 text-slate-400" /> Call Recording
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              {recordings.length > 0 ? (
                <div className="space-y-3">
                  {recordings.map((rec, i) => (
                    <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] text-slate-400">Recording {rec.sid?.slice(-6)}</span>
                        <span className="text-[11px] text-slate-400">{rec.duration}s</span>
                      </div>
                      <audio controls className="w-full h-8" data-testid={`recording-player-${i}`}>
                        <source src={rec.url} type="audio/mpeg" />
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  ))}
                </div>
              ) : call?.recording_url ? (
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] text-slate-400">Call recording</span>
                    <span className="text-[11px] text-slate-400">{call.recording_duration}s</span>
                  </div>
                  <audio controls className="w-full h-8" data-testid="recording-player-0">
                    <source src={call.recording_url} type="audio/mpeg" />
                  </audio>
                </div>
              ) : (
                <p className="text-[12px] text-slate-400 text-center py-3">No recording available for this call</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
